import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import type { FundSource } from '~/schemas/fundSources'
import { createFundSourceSchema } from '~/schemas/fundSources'

export const useFundSourcesStore = defineStore('fund-sources', () => {
  const { get, post, put, delete: del } = useAuthenticatedFetch()
  const log = useLogger('store:fundSources')
  const businessStore = useBusinessStore()
  const { businessId } = storeToRefs(businessStore)
  const nuxtApp = useNuxtApp()
  const { t } = nuxtApp.$i18n
  const { $dialog, $confirm } = nuxtApp

  // State
  const fundSources: Ref<FundSource[]> = ref([])
  const loading: Ref<boolean> = ref(false)

  // Fetch fund sources
  async function fetchFundSources(): Promise<void> {
    if (!businessId.value) {
      log.error('Business ID not found')
      return
    }

    loading.value = true
    try {
      const res = await get(`/api/business/${businessId.value}/expenses/fund-sources`)
      // Use the data array directly (contains all fund sources for management)
      fundSources.value = res.data || []
    }
    catch (error) {
      log.error('Failed to fetch fund sources:', error)
      useErrorToast('Failed to load fund sources')
    }
    finally {
      loading.value = false
    }
  }

  // Create fund source
  async function createFundSource(formData: Partial<FundSource>): Promise<boolean> {
    if (!businessId.value) return false

    const data = {
      ...formData,
      business_id: businessId.value,
    }

    const validatedData = createFundSourceSchema.safeParse(data)

    if (!validatedData.success) {
      log.error('Validation failed:', validatedData.error)
      useErrorToast('Please check the form data and try again')
      return false
    }

    try {
      const res = await post(`/api/business/${businessId.value}/expenses/fund-sources`, validatedData.data)

      if (res?.success) {
        useSuccessToast('Fund source created successfully')
        await fetchFundSources()
        return true
      }
      return false
    }
    catch (error: any) {
      log.error('Failed to create fund source:', error)
      useErrorToast(error?.data?.message || 'Failed to create fund source')
      return false
    }
  }

  // Update fund source
  async function updateFundSource(id: number, formData: Partial<FundSource>): Promise<boolean> {
    if (!businessId.value) return false

    try {
      const res = await put(`/api/business/${businessId.value}/expenses/fund-sources/${id}`, formData)

      if (res?.success) {
        useSuccessToast('Fund source updated successfully')
        await fetchFundSources()
        return true
      }
      return false
    }
    catch (error: any) {
      log.error('Failed to update fund source:', error)
      useErrorToast(error?.data?.message || 'Failed to update fund source')
      return false
    }
  }

  // Delete fund source with confirmation
  async function deleteFundSource(id: number): Promise<void> {
    if (!businessId.value) return

    $confirm.require({
      group: 'inline',
      message: t('Do you want to delete this fund source?'),
      header: t('Confirmation'),
      icon: 'pi pi-exclamation-triangle',
      rejectProps: {
        label: t('Cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptProps: {
        label: t('Delete'),
        severity: 'danger',
      },
      accept: async () => {
        try {
          const res = await del(`/api/business/${businessId.value}/expenses/fund-sources/${id}`)

          if (res?.success) {
            useSuccessToast('Fund source deleted successfully')
            await fetchFundSources()
          }
        }
        catch (error: any) {
          log.error('Failed to delete fund source:', error)
          const message = error?.data?.statusMessage || error?.data?.message
          if (message?.includes('in use')) {
            useErrorToast('Cannot delete fund source that is in use')
          }
          else {
            useErrorToast(message || 'Failed to delete fund source')
          }
        }
      },
    })
  }

  // Toggle active status
  async function toggleActive(id: number, active: boolean): Promise<boolean> {
    const fundSource = fundSources.value.find(fs => fs.id === id)
    if (!fundSource) return false

    return await updateFundSource(id, {
      name: fundSource.name,
      description: fundSource.description || null,
      active,
    })
  }

  // Dialog methods
  async function openCreateDialog(): Promise<void> {
    const { default: CreateModal } = await import('~/components/Admin/FundSources/CreateModal.vue')

    $dialog.open(CreateModal, {
      props: {
        header: t('Add new fund source'),
        ...useDynamicDialogSettings,
      },
      onClose: async () => {
        await fetchFundSources()
      },
    })
  }

  async function openEditDialog(fundSource: FundSource): Promise<void> {
    const { default: EditModal } = await import('~/components/Admin/FundSources/EditModal.vue')

    $dialog.open(EditModal, {
      data: {
        fundSource,
      },
      props: {
        header: t('Edit fund source'),
        ...useDynamicDialogSettings,
      },
      onClose: async () => {
        await fetchFundSources()
      },
    })
  }

  // Reset store
  function reset(): void {
    fundSources.value = []
    loading.value = false
  }

  return {
    // State
    fundSources,
    loading,

    // Methods
    fetchFundSources,
    createFundSource,
    updateFundSource,
    deleteFundSource,
    toggleActive,

    // Dialog methods
    openCreateDialog,
    openEditDialog,

    // Reset
    reset,
  }
},
{
  persist: {
    key: 'fund-sources',
    storage: localStorage,
    paths: [], // Don't persist anything by default
  },
})
